/**
 * 部署スコープ版の起票（承認起票 leaf の REST 口を叩く）。
 *
 * SpiffArena を直接叩かないのは、承認者を組織構造から解決して起票時に凍結する
 * 必要があるため。解決は leaf（サーバ側）が行い、UI は業務データだけを渡す
 * （部署・承認者を UI から渡せると、利用者が承認者を選べてしまう）。
 */

/** 起票が止まった理由。画面が「再試行」か「管理者に連絡」かを案内するために区別する。 */
export type StartScopedFailureReason =
  | "not_configured"
  | "no_approvers"
  | "approver_resolution_failed"
  | "reserved_key"
  | "initiator_mismatch"
  | "unknown";

export class StartScopedCaseError extends Error {
  readonly reason: StartScopedFailureReason;
  readonly status: number | null;

  constructor(
    reason: StartScopedFailureReason,
    message: string,
    status: number | null = null,
  ) {
    super(message);
    this.name = "StartScopedCaseError";
    this.reason = reason;
    this.status = status;
  }
}

export type StartScopedCaseInput = {
  /** メッセージ開始イベントの message 名。 */
  readonly messageName: string;
  /** 承認レーン名（BPMN の lane）。 */
  readonly lanes: readonly string[];
  /** 起票フォームの入力値。payload のトップレベルに載る。 */
  readonly businessData: Readonly<Record<string, unknown>>;
};

export type StartScopedCaseDeps = {
  /**
   * 承認起票 leaf のオリジン。**空なら同一オリジン**（相対パス）で叩く。
   * 本番は CloudFront が `/approval/*` を leaf へ通すので空のままでよい。
   * ローカルで leaf を別ポートで動かす場合だけ指定する。
   */
  readonly baseUrl: string;
  /**
   * SpiffArena が set した `access_token` cookie の値（`Authorization: Bearer`
   * にそのまま載せる）。leaf はこれを申請者の委任トークンとして検証する。
   */
  readonly accessToken: string;
  readonly fetchImpl?: typeof fetch;
};

/** leaf が返す理由コード → UI 側の理由。未知の値は unknown に寄せる。 */
const REASONS: Record<string, StartScopedFailureReason> = {
  no_approvers: "no_approvers",
  approver_resolution_failed: "approver_resolution_failed",
  reserved_key: "reserved_key",
  initiator_mismatch: "initiator_mismatch",
};

export async function startScopedCase(
  input: StartScopedCaseInput,
  deps: StartScopedCaseDeps,
): Promise<{ readonly caseId: number }> {
  const fetchImpl = deps.fetchImpl ?? globalThis.fetch;
  const response = await fetchImpl(
    `${deps.baseUrl.replace(/\/$/, "")}/approval/start`,
    {
      method: "POST",
      headers: {
        authorization: `Bearer ${deps.accessToken}`,
        "content-type": "application/json",
      },
      body: JSON.stringify({
        message_name: input.messageName,
        approval_lanes: input.lanes.map((lane) => ({ lane })),
        business_data: input.businessData,
      }),
    },
  );

  // JSON でない応答（502 の HTML 等）でも落ちないようにする
  const body = (await response.json().catch(() => null)) as {
    error?: string;
    processInstanceId?: number;
  } | null;

  if (!response.ok) {
    // leaf を配っていない環境では behavior が無く、SPA のフォールバックや S3 に
    // 流れて 404/405 になる。再試行しても直らないので設定漏れとして案内する
    const reason =
      response.status === 404 || response.status === 405
        ? "not_configured"
        : (REASONS[body?.error ?? ""] ?? "unknown");
    throw new StartScopedCaseError(
      reason,
      `起票に失敗しました（status ${response.status}）。`,
      response.status,
    );
  }

  if (typeof body?.processInstanceId !== "number") {
    // 起票できたか判断できない。成功として扱うと案件を二重に作らせてしまう。
    throw new StartScopedCaseError(
      "unknown",
      "起票の応答に案件 id がありません。",
      response.status,
    );
  }

  return { caseId: body.processInstanceId };
}
